import { useEffect, useState, useRef } from 'react';
import { useOrdersStore } from '@/store/orders';
import { chatApi } from '@/lib/api';
import { wsClient } from '@/lib/ws';
import { WS_CHANNELS } from '@/lib/constants';
import { logger } from '@/lib/logger';
import { formatTime } from '@/lib/format';
import { MessageCircle, Send, User, Package } from 'lucide-react';
import { useAuthStore } from '@/store/auth';

interface ChatMessage {
  id: string;
  thread_id: string;
  sender_id: string;
  sender_role: string;
  message: string;
  created_at: string;
}

interface ChatThread {
  threadId: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount: number;
}

export default function ChatPage() {
  const { user } = useAuthStore();
  const { activeOrders } = useOrdersStore();
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<ChatThread | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize threads from active orders
  useEffect(() => {
    const initializeThreads = async () => {
      const orderThreads: ChatThread[] = [];

      for (const order of activeOrders) {
        try {
          const result = await chatApi.createOrGetThread(order.id);
          orderThreads.push({
            threadId: result.threadId,
            orderId: order.id,
            orderNumber: `#${order.id.slice(0, 6).toUpperCase()}`,
            customerName: order.customerName || 'Customer',
            unreadCount: 0,
          });
        } catch (error) {
          logger.error('Failed to create/get thread', error as Error, { orderId: order.id });
        }
      }

      setThreads(orderThreads);
    };

    if (activeOrders.length > 0) {
      initializeThreads();
    }
  }, [activeOrders]);

  // Load messages when thread is selected
  useEffect(() => {
    if (!selectedThread) return;

    const loadMessages = async () => {
      setIsLoadingMessages(true);
      try {
        const result = await chatApi.getMessages(selectedThread.threadId);
        setMessages(result.messages || []);
      } catch (error) {
        logger.error('Failed to load messages', error as Error);
      } finally {
        setIsLoadingMessages(false);
      }
    };

    loadMessages();
  }, [selectedThread]);

  // Subscribe to WebSocket chat channel
  useEffect(() => {
    const handleChatMessage = (data: any) => {
      logger.info('Chat message received', { data });

      if (data.type === 'chat:new_message') {
        // Add message if it's for the current thread
        if (selectedThread && data.payload.threadId === selectedThread.threadId) {
          const newMsg: ChatMessage = {
            id: data.payload.messageId,
            thread_id: data.payload.threadId,
            sender_id: data.payload.senderId,
            sender_role: data.payload.senderRole,
            message: data.payload.message,
            created_at: data.timestamp,
          };

          setMessages((prev) => [...prev, newMsg]);
        }
      } else if (data.type === 'chat:typing') {
        // Handle typing indicator
        if (selectedThread && data.payload.threadId === selectedThread.threadId) {
          if (data.payload.isTyping && data.payload.userId !== user?.id) {
            setTypingUsers((prev) => new Set(prev).add(data.payload.userId));

            // Clear typing after 3 seconds
            setTimeout(() => {
              setTypingUsers((prev) => {
                const next = new Set(prev);
                next.delete(data.payload.userId);
                return next;
              });
            }, 3000);
          } else {
            setTypingUsers((prev) => {
              const next = new Set(prev);
              next.delete(data.payload.userId);
              return next;
            });
          }
        }
      }
    };

    wsClient.subscribe(WS_CHANNELS.CHAT, handleChatMessage);

    return () => {
      wsClient.unsubscribe(WS_CHANNELS.CHAT, handleChatMessage);
    };
  }, [selectedThread, user]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newMessage.trim() || !selectedThread || isSending) return;

    setIsSending(true);
    try {
      await chatApi.sendMessage(selectedThread.threadId, newMessage.trim());
      setNewMessage('');

      // Stop typing indicator
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      await chatApi.sendTyping(selectedThread.threadId, false);
    } catch (error) {
      logger.error('Failed to send message', error as Error);
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleTyping = async () => {
    if (!selectedThread) return;

    // Send typing indicator
    try {
      await chatApi.sendTyping(selectedThread.threadId, true);
    } catch (error) {
      logger.error('Failed to send typing indicator', error as Error);
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing after 3 seconds
    typingTimeoutRef.current = setTimeout(async () => {
      try {
        await chatApi.sendTyping(selectedThread.threadId, false);
      } catch (error) {
        logger.error('Failed to stop typing indicator', error as Error);
      }
    }, 3000);
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex">
      {/* Threads Sidebar */}
      <div className="w-80 border-r border-border bg-card flex flex-col">
        <div className="p-4 border-b border-border">
          <h2 className="text-xl font-bold text-foreground">Conversations</h2>
          <p className="text-sm text-muted-foreground">Order-based chats with customers</p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {threads.length === 0 ? (
            <div className="p-8 text-center">
              <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No active conversations</p>
              <p className="text-sm text-muted-foreground mt-2">
                Accept an order to start chatting
              </p>
            </div>
          ) : (
            threads.map((thread) => (
              <button
                key={thread.threadId}
                onClick={() => setSelectedThread(thread)}
                className={`w-full p-4 border-b border-border text-left hover:bg-muted transition-colors ${
                  selectedThread?.threadId === thread.threadId ? 'bg-muted' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-foreground">{thread.customerName}</span>
                      {thread.unreadCount > 0 && (
                        <span className="w-5 h-5 bg-primary text-primary-foreground rounded-full text-xs flex items-center justify-center">
                          {thread.unreadCount}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Package className="w-3 h-3" />
                      <span>{thread.orderNumber}</span>
                    </div>
                    {thread.lastMessage && (
                      <p className="text-sm text-muted-foreground truncate mt-1">
                        {thread.lastMessage}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col bg-background">
        {selectedThread ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-border bg-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{selectedThread.customerName}</h3>
                  <p className="text-sm text-muted-foreground">Order {selectedThread.orderNumber}</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {isLoadingMessages ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">Loading messages...</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No messages yet</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Start the conversation with your customer
                  </p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isOwn = msg.sender_id === user?.id;

                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg p-3 ${
                          isOwn
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-foreground'
                        }`}
                      >
                        {!isOwn && (
                          <p className="text-xs font-medium mb-1 opacity-70">
                            {msg.sender_role === 'buyer' ? 'Customer' : msg.sender_role}
                          </p>
                        )}
                        <p className="text-sm">{msg.message}</p>
                        <p
                          className={`text-xs mt-1 ${
                            isOwn ? 'opacity-70' : 'text-muted-foreground'
                          }`}
                        >
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}

              {/* Typing Indicator */}
              {typingUsers.size > 0 && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-lg p-3 text-sm text-muted-foreground">
                    <span className="animate-pulse">Customer is typing...</span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-border bg-card">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    handleTyping();
                  }}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={isSending}
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || isSending}
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  <span>{isSending ? 'Sending...' : 'Send'}</span>
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                No Conversation Selected
              </h3>
              <p className="text-muted-foreground">
                Select a conversation from the list to start chatting
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
